import styled from 'styled-components';
import { P, card, eyebrow, onPhone, radius, shadow, tint, tintB, tintT } from '../../theme/tokens';

const btnReset = `
  border: none;
  font-family: inherit;
  text-align: left;
  cursor: pointer;
`;

/* ---------------------------------------------------------------- hero */
export const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  max-width: 1240px;
`;

export const Hero = styled.section`
  position: relative;
  overflow: hidden;
  border-radius: ${radius.xl}px;
  box-shadow: ${shadow.md};
  background: ${P.grad};
  color: ${P.white};
  padding: 22px 26px;

  button:focus-visible {
    outline: 2px solid ${P.white};
    outline-offset: 2px;
  }
`;

export const Glow = styled.div`
  position: absolute;
  inset: 0;
  overflow: hidden;
  background: radial-gradient(120% 140% at 78% 120%, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0) 60%);
  pointer-events: none;
`;

export const HeroIntro = styled.div`
  position: relative;
  min-width: 0;

  h2 {
    margin: 6px 0 2px;
    font-size: 24px;
    font-weight: 700;
    color: ${P.white};
    letter-spacing: -0.01em;
  }
`;

export const Eyebrow = styled.div`
  ${eyebrow}
  display: flex;
  align-items: center;
  gap: 8px;
  letter-spacing: 0.16em;
  color: rgba(255, 255, 255, 0.62);

  svg {
    font-size: 11px;
  }
`;

export const HeroSub = styled.p`
  margin: 0;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.84);
  line-height: 1.5;
`;

export const Recap = styled.div`
  position: relative;
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

export const Kpi = styled.button`
  ${btnReset}
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: ${radius.lg}px;
  padding: 12px 14px;
  color: ${P.white};
  min-height: 48px;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }

  .k-label {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #c9d1dd;
  }
  .k-value {
    font-size: 30px;
    font-weight: 700;
    line-height: 1.15;
    font-variant-numeric: tabular-nums;
  }
  .k-sub {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    color: #d6dde7;

    svg {
      font-size: 9px;
    }
  }
  ${onPhone} {
    padding: 10px 12px;
    .k-label {
      font-size: 11px;
    }
    .k-value {
      font-size: 22px;
    }
  }
`;

/* ---------------------------------------------------------------- pointer */
const TONE = {
  red: { bg: '#FFF2F0', border: '#F5C2C7', color: '#A8071A' },
  amber: { bg: '#FFF7E6', border: '#F3DDB0', color: '#7A4A00' },
  ok: { bg: '#F6FFED', border: '#C7EBB3', color: '#237804' }
};

export const Pointer = styled.button`
  ${btnReset}
  display: flex;
  gap: 12px;
  align-items: center;
  width: 100%;
  min-height: 56px;
  padding: 12px 16px;
  border-radius: ${radius.lg}px;
  border: 1px solid ${(p) => TONE[p.$tone].border};
  background: ${(p) => TONE[p.$tone].bg};
  color: ${(p) => TONE[p.$tone].color};

  > svg {
    font-size: 18px;
    flex-shrink: 0;
  }
  .p-grow {
    flex: 1;
    min-width: 0;
  }
  .p-title {
    font-weight: 700;
  }
  .p-sub {
    font-size: 12.5px;
    opacity: 0.9;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  ${onPhone} {
    .p-title {
      font-size: 13px;
    }
    .p-sub {
      font-size: 13px;
    }
    .p-open {
      font-size: 13px;
    }
  }
  .p-open {
    display: flex;
    align-items: center;
    gap: 6px;
    font-weight: 700;
    flex-shrink: 0;

    svg {
      font-size: 11px;
    }
  }
`;

/* ---------------------------------------------------------------- small cards */
export const SmallGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
  ${onPhone} {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
    > :last-child:nth-child(odd) {
      grid-column: 1 / -1;
    }
  }
`;

export const SmallCard = styled.section`
  ${card}
  padding: 14px;
  min-width: 0;

  .sc-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
  .sc-title {
    flex: 1;
    min-width: 0;
    font-size: 14px;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .sc-big {
    font-size: 22px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
  }
  .sc-sub {
    font-size: 12.5px;
    color: ${P.muted};
    margin-top: 2px;
  }
  ${onPhone} {
    padding: 12px;
    .sc-head {
      gap: 8px;
      margin-bottom: 6px;
    }
    .sc-title {
      font-size: 13px;
    }
    .sc-head > :first-child {
      display: none;
    }
    .sc-big {
      font-size: 16px;
    }
    .sc-sub {
      margin-top: 2px;
      font-size: 12px;
    }
  }
`;

export const Bubble = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 10px;
  flex-shrink: 0;
  display: grid;
  place-items: center;
  background: ${(p) => (p.$color ? tint(p.$color) : '#E6EAF0')};
  color: ${(p) => p.$color || P.navyInk};
  font-size: 14px;

  &.sm {
    width: 30px;
    height: 30px;
    border-radius: 8px;
    font-size: 13px;
  }
`;

export const HeadLink = styled.button`
  ${btnReset}
  background: none;
  padding: 0;
  font-size: 13px;
  font-weight: 600;
  color: ${P.navy};
  flex-shrink: 0;

  &:hover {
    text-decoration: underline;
  }
`;

/* ---------------------------------------------------------------- section card */
export const Section = styled.section`
  ${card}
  padding: 18px;
  min-width: 0;
  ${onPhone} {
    padding: 14px;
  }
`;

export const SectionHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 14px;

  h3 {
    flex: 1;
    min-width: 0;
    margin: 0;
    font-size: 16px;
    font-weight: 700;
    color: ${P.ink};
  }
  .sh-sub {
    font-size: 12.5px;
    color: ${P.muted};
    font-weight: 400;
  }
  ${onPhone} {
    gap: 8px;
    margin-bottom: 10px;
    h3 {
      font-size: 15px;
    }
    > :first-child:not(h3) {
      display: none;
    }
  }
`;

/* ---------------------------------------------------------------- week strip */
export const Week = styled.div`
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  gap: 8px;

  @media (max-width: 768px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const WeekDay = styled.div`
  background: ${P.bgSubtle};
  border: 1px solid ${P.line};
  border-radius: 10px;
  padding: 8px;
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-height: 150px;
  min-width: 0;

  ${(p) =>
    p.$today &&
    `border-color: ${P.navy}; background: ${P.white}; box-shadow: 0 0 0 1px ${P.navy};`}

  @media (max-width: 768px) {
    min-height: 0;
  }

  .wd-head {
    ${btnReset}
    display: flex;
    gap: 6px;
    align-items: center;
    background: none;
    padding: 0;
    font-size: 13px;

    .wd-dow {
      font-weight: 700;
    }
    .wd-num {
      font-variant-numeric: tabular-nums;
      color: ${P.muted};
    }
    .wd-today {
      margin-left: auto;
      font-size: 11px;
      font-weight: 700;
      color: ${P.white};
      background: ${P.navy};
      border-radius: 999px;
      padding: 1px 8px;
    }
  }
  .wd-empty {
    font-size: 12px;
    color: ${P.subtle};
  }
  ${onPhone} {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 10px;
    padding: 8px 10px;
    .wd-head {
      flex: 1 1 auto;
      .wd-today {
        font-size: 12px;
      }
    }
  }
`;

export const EventChip = styled.button`
  ${btnReset}
  display: flex;
  gap: 5px;
  align-items: center;
  width: 100%;
  border-radius: 6px;
  padding: 4px 6px;
  min-height: 28px;
  font-size: 11.5px;
  font-weight: 600;
  background: ${(p) => tint(p.$color)};
  color: ${(p) => tintT(p.$color)};
  border: 1px solid ${(p) => tintB(p.$color)};

  svg {
    font-size: 10px;
    flex-shrink: 0;
  }
  .ev-time {
    font-variant-numeric: tabular-nums;
    flex-shrink: 0;
  }
  .ev-title {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
  }
`;

/* ---------------------------------------------------------------- two column */
export const TwoCol = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1.6fr) minmax(0, 1fr);
  gap: 16px;
  align-items: start;

  @media (max-width: 900px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const TrendSub = styled.p`
  margin: 10px 0 0;
  font-size: 12.5px;
  color: ${P.muted};
`;

export const ComingRow = styled.button`
  ${btnReset}
  display: flex;
  gap: 10px;
  align-items: center;
  width: 100%;
  padding: 8px 0;
  background: none;
  border-bottom: 1px solid ${P.line};
  min-height: 44px;
  color: ${P.ink};

  &:last-child {
    border-bottom: none;
  }
  &:hover .cr-title {
    color: ${P.navy};
  }
  .cr-when {
    font-size: 12px;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: ${P.muted};
    white-space: nowrap;
    flex-shrink: 0;
    width: 86px;
  }
  .cr-title {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const Muted = styled.div`
  font-size: 13px;
  color: ${P.subtle};
  padding: ${(p) => (p.$pad ? '24px 8px' : '0')};
  text-align: ${(p) => (p.$pad ? 'center' : 'left')};
`;
